import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-sage-green text-primary-foreground hover:bg-sage-green-dark shadow-soft hover:shadow-card",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-background hover:bg-muted hover:border-sage-green/30",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-sage-green underline-offset-4 hover:underline",
        hero: "bg-sage-green text-primary-foreground shadow-elevated hover:shadow-card hover:scale-[1.02] active:scale-[0.98]",
        heroOutline: "border-2 border-sage-green/30 bg-background/80 backdrop-blur-sm text-foreground hover:bg-sage-green/5 hover:border-sage-green/50 shadow-soft",
        gold: "bg-mustard text-accent-foreground shadow-soft hover:brightness-110 hover:scale-[1.02] active:scale-[0.98]",
        crisis: "bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground",
        sage: "bg-sage-green-light text-sage-green-dark hover:bg-sage-green/20 border border-sage-green/20",
        purple: "bg-sage-green text-primary-foreground shadow-lg shadow-sage-green/25 hover:bg-sage-green-dark hover:shadow-sage-green/40 hover:scale-[1.02] active:scale-[0.98]",
        purpleOutline: "border-2 border-sage-green/40 bg-sage-green-light/20 text-sage-green-dark hover:bg-sage-green/10 hover:border-sage-green/60",
        purpleGlow: "bg-sage-green text-primary-foreground shadow-card hover:shadow-elevated hover:brightness-105 hover:scale-[1.02] active:scale-[0.98]",
      },
      size: {
        default: "h-12 px-8 py-3",
        sm: "h-10 px-6 py-2 text-sm",
        lg: "h-14 px-10 py-3 text-base",
        xl: "h-16 px-12 py-4 text-lg",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
